const DRAFT_TTL = 15 * 60 * 1000;
const FAILURE_MESSAGE = "We couldn't confirm your request. Please try again.";

class COARequest extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.form = this.querySelector('form');
    this.fields = [...this.querySelectorAll('[data-coa-field]')];
    this.button = this.querySelector('[data-coa-submit]');
    this.error = this.querySelector('[data-coa-error]');
    this.status = this.querySelector('[data-coa-status]');
    this.pending = false;
    this.attempt = 0;
    if (this.querySelector('[data-coa-success]')) {
      this.clearDraft();
      this.querySelector('[data-coa-success]').focus();
      return;
    }
    if (!this.form || !this.button) return;
    this.restoreDraft();
    this.form.addEventListener('submit', this.handleSubmit, { signal });
    this.form.addEventListener('input', (event) => {
      if (this.fields.includes(event.target)) event.target.removeAttribute('aria-invalid');
    }, { signal });
    window.addEventListener('pageshow', (event) => { if (event.persisted) this.resetPending(); }, { signal });
    window.addEventListener('pagehide', () => clearTimeout(this.timer), { signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
    clearTimeout(this.timer);
    clearTimeout(this.expiryTimer);
    this.attempt++;
  }

  clearDraft() {
    clearTimeout(this.expiryTimer);
    try { sessionStorage.removeItem(this.dataset.storageKey); } catch { /* Storage may be disabled. */ }
  }

  saveDraft() {
    const fields = Object.fromEntries(this.fields.map(field => [field.name, field.value]));
    try {
      sessionStorage.setItem(this.dataset.storageKey, JSON.stringify({ expires: Date.now() + DRAFT_TTL, fields }));
      clearTimeout(this.expiryTimer);
      this.expiryTimer = setTimeout(() => this.clearDraft(), DRAFT_TTL);
    } catch { /* Native submission remains available when storage is disabled. */ }
  }

  restoreDraft() {
    try {
      const draft = JSON.parse(sessionStorage.getItem(this.dataset.storageKey) || 'null');
      if (!draft || !Number.isFinite(draft.expires) || draft.expires <= Date.now()) {
        this.clearDraft();
        return;
      }
      for (const field of this.fields) {
        const value = draft.fields?.[field.name];
        if (typeof value === 'string') field.value = value.slice(0, field.maxLength > 0 ? field.maxLength : 2000);
      }
      this.expiryTimer = setTimeout(() => this.clearDraft(), draft.expires - Date.now());
    } catch { this.clearDraft(); }
  }

  resetPending() {
    this.attempt++;
    this.pending = false;
    clearTimeout(this.timer);
    this.form?.removeAttribute('aria-busy');
    for (const field of this.fields) field.readOnly = false;
    if (this.button) {
      this.button.disabled = false;
      this.button.textContent = 'REQUEST COA';
    }
    if (this.status) this.status.textContent = '';
  }

  fail() {
    this.resetPending();
    this.error.textContent = FAILURE_MESSAGE;
    this.error.hidden = false;
    this.error.focus();
  }

  handleSubmit = (event) => {
    event.preventDefault();
    if (this.pending) return;
    for (const field of this.fields) {
      field.value = field.value.trim();
      field.setAttribute('aria-invalid', String(!field.checkValidity()));
    }
    if (!this.form.reportValidity()) return;
    this.saveDraft();
    if (navigator.onLine === false) {
      this.fail();
      return;
    }
    this.pending = true;
    const attempt = ++this.attempt;
    this.button.disabled = true;
    this.button.textContent = 'SUBMITTING...';
    this.form.setAttribute('aria-busy', 'true');
    for (const field of this.fields) field.readOnly = true;
    this.error.hidden = true;
    this.status.textContent = 'Submitting your request for manual review.';
    this.timer = setTimeout(() => { if (this.pending && this.attempt === attempt) this.fail(); }, 60000);

    // Use Shopify's supported CAPTCHA integration and native navigation, not a simulated AJAX success.
    let submitted = false;
    const submit = () => {
      if (submitted || !this.pending || this.attempt !== attempt || !this.isConnected) return;
      submitted = true;
      try { this.form.submit(); } catch { this.fail(); }
    };
    try {
      if (window.Shopify?.captcha?.protect) window.Shopify.captcha.protect(this.form, submit);
      else submit();
    } catch { this.fail(); }
  };
}

if (!customElements.get('coa-request')) customElements.define('coa-request', COARequest);
